import React from 'react';
import { AntdConfig } from '@react-awesome-query-builder/antd';
import { BasicFuncs } from '@react-awesome-query-builder/ui';
import ru_RU from 'antd/es/locale/ru_RU';
import CustomMultiSelect from './QueryBuilder/CustomMultiSelect';
import FieldSelectWithSubtitle from './QueryBuilder/FieldSelectWithSubtitle';
import { Select } from 'antd';

const { Option } = Select;
const InitialConfig = AntdConfig;

export const booleanOperators = ['equal', 'not_equal'];
export const datetimeOperators = [
  'equal',
  'not_equal',
  'less',
  'less_or_equal',
  'greater',
  'greater_or_equal'
];
export const numberOperators = [...datetimeOperators, 'is_in_range'];
export const textOperators = [
  'equal',
  'not_equal',
  'like',
  'starts_with',
  'ends_with',
  'not_like',
  'is_in',
  'is_in_lookup_list'
];

const conjunctions = {
  ...InitialConfig.conjunctions
};
const proximity = {
  ...InitialConfig.operators.proximity,
  valueLabels: [
    { label: 'Word 1', placeholder: 'Enter first word' },
    { label: 'Word 2', placeholder: 'Enter second word' }
  ],
  textSeparators: [
    //'Word 1',
    //'Word 2'
  ],
  options: {
    ...InitialConfig.operators.proximity.options,
    optionLabel: 'Near', // label on top of "near" selectbox (for config.settings.showLabels==true)
    optionTextBefore: 'Near', // label before "near" selectbox (for config.settings.showLabels==false)
    optionPlaceholder: 'Select words between', // placeholder for "near" selectbox
    minProximity: 2,
    maxProximity: 10,
    defaults: {
      proximity: 2
    },
    customProps: {}
  }
};

// FIN-12286 — the IN-clause value can arrive as an array (the list_text /
// multiselect widget emits array values) or as a legacy comma-string. The old
// `values.replace(...)` assumed a string and threw "values.replace is not a
// function" for array values, which crashed the query-builder through
// Utils.checkTree (see QueryBuilder treeValue useMemo). Normalise both shapes:
// strip surrounding quotes, trim, drop blanks, then join.
const formatInClause = (field, values) => {
  const parts = Array.isArray(values)
    ? values
    : String(values ?? '').split(',');
  const cleaned = parts
    .map((word) => String(word).replace(/["']/g, '').trim())
    .filter((word) => word !== '')
    .join(',');
  return `${field} IN (${cleaned})`;
};

const operators = (isFieldWithFunction) => ({
  ...InitialConfig.operators,
  // examples of  overriding
  proximity,
  between: {
    ...InitialConfig.operators.between,
    valueLabels: ['Value from', 'Value to'],
    textSeparators: ['from', 'to']
  },
  is_in: {
    isNotOp: false,
    label: 'Is In',
    reversedOp: 'sl_not_in',
    labelForFormat: 'IN',
    sqlOp: 'IN',
    valueSources: ['value'],
    mongoFormatOp: '',
    jsonLogic: 'in',
    formatOp: (
      field,
      op,
      values,
      valueSrc,
      valueType,
      opDef,
      operatorOptions,
      isForDisplay
    ) => {
      return formatInClause(field, values);
    },
    sqlFormatOp: (
      field,
      op,
      values,
      valueSrc,
      valueType,
      opDef,
      operatorOptions,
      fieldDef
    ) => {
      if (valueSrc == 'value') {
        return formatInClause(field, values);
      }
    }
  },
  is_in_lookup_list: {
    isNotOp: false,
    label: 'Is In Lookup List',
    reversedOp: 'sl_not_in',
    labelForFormat: 'IN',
    sqlOp: 'IN',
    valueSources: ['value'],
    mongoFormatOp: '',
    jsonLogic: 'in',
    formatOp: (
      field,
      op,
      values,
      valueSrc,
      valueType,
      opDef,
      operatorOptions,
      isForDisplay
    ) => {
      return formatInClause(field, values);
    },
    sqlFormatOp: (
      field,
      op,
      values,
      valueSrc,
      valueType,
      opDef,
      operatorOptions,
      fieldDef
    ) => {
      if (valueSrc == 'value') {
        return formatInClause(field, values);
      }
    }
  },
  // Show this operator only formula tab.
  // isFieldWithFunction is true only formula tab other its false.
  ...(isFieldWithFunction && {
    is_in_range: {
      label: 'Is In Range',
      labelForFormat: 'isInRange',
      valueSources: ['field']
    }
  })
});

const widgetsData = (matchList = [], allFunc = []) => ({
  ...InitialConfig.widgets,
  // examples of overriding
  text: {
    ...InitialConfig.widgets.text
  },
  textarea: {
    ...InitialConfig.widgets.textarea,
    maxRows: 3
  },
  slider: {
    ...InitialConfig.widgets.slider
  },
  rangeslider: {
    ...InitialConfig.widgets.rangeslider
  },
  date: {
    ...InitialConfig.widgets.date,
    dateFormat: 'DD.MM.YYYY',
    valueFormat: 'YYYY-MM-DD'
  },
  time: {
    ...InitialConfig.widgets.time,
    timeFormat: 'HH:mm',
    valueFormat: 'HH:mm:ss'
  },
  datetime: {
    ...InitialConfig.widgets.datetime,
    timeFormat: 'HH:mm',
    dateFormat: 'DD.MM.YYYY',
    valueFormat: 'YYYY-MM-DDTHH:mm:ss[Z]'
  },
  func: {
    ...InitialConfig.widgets.func,
    customProps: {
      showSearch: true
    }
  },
  select: {
    ...InitialConfig.widgets.select
  },
  multiselect: {
    ...InitialConfig.widgets.multiselect,
    customProps: {
      width: '200px',
      input: {
        width: '100px'
      }
    },
    factory: ({ value, setValue, listValues, placeholder }) => {
      return (
        <Select
          mode="multiple"
          value={(value?.toJS ? value.toJS() : value) || []}
          placeholder={placeholder}
          options={listValues}
          className="ant-select-sm"
          style={{ minWidth: '80px' }}
          onChange={(value) => setValue(value)}
        />
      );
    }
  },
  treeselect: {
    ...InitialConfig.widgets.treeselect,
    customProps: {
      showSearch: true
    }
  },
  list_text: {
    type: 'list_text',
    jsType: 'array',
    valueSrc: 'value',
    valueLabel: 'Values',
    valuePlaceholder: 'Select values',
    formatValue: function (vals, fieldDef, wgtDef, isForDisplay) {
      const treeData = fieldDef.customListValues;
      let valsLabels = vals.map((v) =>
        this.utils.getTitleInListValues(treeData, v)
      );
      return isForDisplay
        ? valsLabels.map(this.utils.stringifyForDisplay)
        : vals.map(JSON.stringify);
    },
    sqlFormatValue: function (vals, fieldDef, wgtDef, op, opDef) {
      return vals.map((v) => this.utils.SqlString.escape(v));
    },
    spelFormatValue: function (val) {
      return this.utils.spelEscape(val);
    },
    toJS: (val) => val,
    factory: (props) => {
      let fieldList = allFunc
        .filter((a) => a.args.aggregationFunction === 'collect_set')
        .map((m) => ({
          title: m.name,
          value: m.name
        }));
      return (
        <CustomMultiSelect
          customListValues={
            props.operator === 'is_in_lookup_list' ? matchList : fieldList
          }
          {...props}
          allowCustomValues={props.operator === 'is_in'}
          multiple={true}
        />
      );
    }
  },
  list_range: {
    type: 'list_range',
    valueSrc: 'field',
    factory: (props) => {
      const fieldList =
        allFunc
          ?.filter((a) => a.args.aggregationFunction === 'density')
          ?.map((m) => ({
            title: m.name,
            value: m.name
          })) || [];

      return (
        <Select
          value={props.value}
          placeholder="Select range"
          className="ant-select-sm"
          onChange={(value) => props.setValue(value)}>
          {fieldList.map((item) => (
            <Option key={item.value} value={item.value}>
              {item.title}
            </Option>
          ))}
        </Select>
      );
    }
  }
});

const types = {
  ...InitialConfig.types,
  text: {
    ...InitialConfig.types.text,
    excludeOperators: ['proximity'],
    widgets: {
      ...InitialConfig.types.text.widgets,
      // multiselect: {
      //   operators: ['is_in'],
      // },
      list_text: {
        operators: ['is_in', 'is_in_lookup_list']
      }
    }
  },
  number: {
    ...InitialConfig.types.number,
    widgets: {
      ...InitialConfig.types.number.widgets,
      list_range: {
        operators: ['is_in_range'],
        valueSource: 'value'
      }
    }
  },
  boolean: {
    ...InitialConfig.types.boolean,
    widgets: {
      ...InitialConfig.types.boolean.widgets,
      boolean: {
        ...InitialConfig.types.boolean.widgets.boolean,
        opProps: {
          equal: {
            label: 'is'
          },
          not_equal: {
            label: 'is not'
          }
        }
      }
    }
  },
  list_text: {
    defaultOperator: 'is_in_lookup_list',
    widgets: {
      list_text: {
        operators: ['is_in_lookup_list']
      }
    }
  }
};

// FIN-12070 — tab-wise delete-confirmation text. The query-builder is shared
// across the Global Conditions / Functions / Formula tabs, so the confirm
// dialog noun must be parametrised per tab ("condition" / "function" /
// "formula"). Default "rule" preserves the original copy for any other usage.
// Each tab threads its noun through QueryBuilder → these options override the
// static `settings` defaults below.
export const buildRemoveConfirmOptions = (itemNoun = 'rule') => ({
  removeRuleConfirmOptions: {
    title: `Are you sure you want to delete this ${itemNoun}?`,
    okText: 'Yes',
    okType: 'danger',
    cancelText: 'Cancel'
  },
  removeGroupConfirmOptions: {
    title: `Are you sure you want to delete this ${itemNoun} group?`,
    okText: 'Yes',
    okType: 'danger',
    cancelText: 'Cancel'
  }
});

const localeSettings = {
  locale: ru_RU,
  valueLabel: 'Value',
  valuePlaceholder: 'Value',
  fieldLabel: 'Field',
  operatorLabel: 'Operator',
  funcLabel: 'Function',
  fieldPlaceholder: 'Select field',
  funcPlaceholder: 'Select function',
  operatorPlaceholder: 'Select operator',
  lockLabel: 'Lock',
  lockedLabel: 'Locked',
  deleteLabel: undefined,
  addGroupLabel: 'Add group',
  addRuleLabel: 'Add rule',
  addSubRuleLabel: 'Add sub rule',
  delGroupLabel: undefined,
  fieldSourcesPopupTitle: 'Select source',
  valueSourcesPopupTitle: 'Select value source',
  ...buildRemoveConfirmOptions()
};

export const settings = {
  ...InitialConfig.settings,
  ...localeSettings,

  defaultSliderWidth: '200px',
  defaultSelectWidth: '200px',
  defaultSearchWidth: '100px',
  defaultMaxRows: 5,

  valueSourcesInfo: {
    value: {
      label: 'Value'
    },
    field: {
      label: 'Field',
      widget: 'field'
    },
    func: {
      label: 'Function',
      widget: 'func'
    }
  },
  fieldSources: ['field', 'func'],
  keepInputOnChangeFieldSrc: true,
  maxNesting: 5,
  canLeaveEmptyGroup: true,
  shouldCreateEmptyGroup: true,
  showErrorMessage: true,
  removeIncompleteRulesOnLoad: false,
  // Keep newly-added empty rules/groups visible until the user completes
  // them. Without these, validateAndFixTree (which runs on every change with
  // these defaults) strips fresh rules immediately → "Add Condition" flickers.
  removeEmptyRulesOnLoad: false,
  removeEmptyGroupsOnLoad: false,
  customFieldSelectProps: {
    showSearch: true
  },
  fieldItemKeysForSearch: ['label', 'tooltip', 'altLabel', 'fullLabel'],
  // FIN-13988 — field ids are dotted `api_path`s in a FLAT map (see trmUtil formatQueryFields), so
  // the default display label, built by splitting the id on `.` and labelling each segment,
  // printed `transaction_event.payee_context.<label>` in the Logic Summary. Use the field's own
  // label. Not `label2`: the picker shows that in place of the api_path hover tooltip.
  formatField: (field, parts, label2, fieldDefinition, config, isForDisplay) =>
    isForDisplay ? fieldDefinition?.label || label2 : field,
  renderField: (props) => React.createElement(FieldSelectWithSubtitle, props)
};

// FIN-12395 — exported so the function catalog (incl. LEFT/RIGHT) is unit-testable
// in isolation without building the full antd query-builder config.
// FIN-13190 — one addend of an arithmetic formula function. An addend may be a
// field, a literal, another function output, or a nested arithmetic function.
// `preferWidgets: ['field']` is required or the number type defaults to the
// list_range widget (same reason LINEAR_REGRESSION.val and MULTIPLE_OF_10.field
// set it).
// FIN-13190 — deliberately has no optional variant. RAQB materialises an unset
// optional func arg as `{valueSrc:'field'}` with no `value` key instead of omitting
// it, which the backend ArgElement decoder rejects, so every arithmetic rule with a
// blank operand slot failed to save. Extra operands come from nesting instead.
const sumArg = (label) => ({
  label,
  type: 'number',
  valueSources: ['field', 'value', 'func'],
  preferWidgets: ['field']
});

// FIN-13190 — renders an arithmetic function as infix for the Logic Summary.
//
// `Utils.queryString` formats a nested operand with that operand's own formatFunc and wraps it in
// the nested function's renderBrackets, so a nested `sum(a, b)` arrives here as "(a + b)". Since
// more than two operands are now expressed by nesting, leaving every layer bracketed would print
// the common case `sum(sum(a, b), c)` as "(a + b) + c" — accurate but noisy. Brackets are
// therefore kept only where they change the reading:
//
//   sum(sum(a, b), c)       -> a + b + c          (associative, same operator: brackets dropped)
//   multiply(sum(a, b), c)  -> (a + b) × c        (mixed operators: brackets carry precedence)
//
// Operands are filtered because one can legitimately be absent while the analyst is still
// building the rule.
// Binding strength, so a nested operand only keeps the brackets that carry meaning. SUBTRACT is
// listed for completeness but never reaches here — its formatFunc renders its two named operands
// directly, which is what keeps `a − (b − c)` from being flattened into the wrong expression.
const OPERATOR_PRECEDENCE = { '+': 1, '−': 1, '×': 2 };

const INFIX_OPERATORS = Object.keys(OPERATOR_PRECEDENCE);

const operatorsIn = (rendered) =>
  INFIX_OPERATORS.filter((op) => rendered.includes(` ${op} `));

/** True when `rendered` is one bracketed group rather than two of them side by side. */
const isSingleBracketedGroup = (rendered) => {
  if (!(rendered.startsWith('(') && rendered.endsWith(')'))) return false;
  let depth = 0;
  for (let i = 0; i < rendered.length; i += 1) {
    if (rendered[i] === '(') depth += 1;
    if (rendered[i] === ')') {
      depth -= 1;
      // Closed before the end, so the leading "(" was not the outer group's.
      if (depth === 0 && i < rendered.length - 1) return false;
    }
  }
  return depth === 0;
};

const bracketAsNeeded = (rendered, operator) => {
  const bracketed = isSingleBracketedGroup(rendered);
  const inner = bracketed ? rendered.slice(1, -1) : rendered;
  const innerOperators = operatorsIn(inner);

  // Brackets are redundant when every operator inside binds at least as tightly as this one and
  // regrouping cannot change the value: `x` inside `+` reads correctly without them
  // (`a x b + c`), and so does the same commutative operator repeated (`a + b + c`). Anything
  // else keeps them, so `multiply(sum(a, b), c)` stays `(a + b) x c`.
  const isRedundant = innerOperators.every(
    (op) =>
      OPERATOR_PRECEDENCE[op] > OPERATOR_PRECEDENCE[operator] || op === operator
  );

  if (isRedundant) return inner;
  return bracketed ? rendered : `(${inner})`;
};

const formatInfix = (args, separator) => {
  const operator = separator.trim();
  return Object.values(args)
    .filter((v) => v !== null && v !== undefined && v !== '')
    .map((v) => bracketAsNeeded(String(v), operator))
    .join(separator);
};

export const funcs = (functionNames) => ({
  //...BasicFuncs
  string: {
    type: '!struct',
    label: 'String',
    subfields: {
      LOWER: {
        ...BasicFuncs.LOWER,
        label: functionNames?.LOWER || 'Lowercase',
        operators: textOperators,
        renderBrackets: ['\u00A0(', '\u00A0)'],
        fieldSettings: {
          maxLength: 50
        },
        args: {
          str: {
            label: 'String',
            type: 'text',
            valueSources: ['value', 'field'],
            fieldSettings: {
              maxLength: 50
            }
          }
        }
      },
      UPPER: {
        ...BasicFuncs.UPPER,
        label: functionNames?.UPPER || 'Uppercase',
        operators: textOperators,
        renderBrackets: ['\u00A0(', '\u00A0)'],
        fieldSettings: {
          maxLength: 50
        },
        args: {
          str: {
            label: 'String',
            type: 'text',
            valueSources: ['value', 'field'],
            fieldSettings: {
              maxLength: 50
            }
          }
        }
      },
      CONCAT: {
        label: functionNames?.CONCAT || 'Concat',
        returnType: 'text',
        jsonLogic: 'concat',
        operators: textOperators,
        renderBrackets: ['\u00A0(', '\u00A0)'],
        fieldSettings: {
          maxLength: 150
        },
        args: {
          str1: {
            label: 'String',
            type: 'text',
            valueSources: ['value', 'field'],
            fieldSettings: {
              maxLength: 50
            }
          },
          str2: {
            label: 'String',
            type: 'text',
            valueSources: ['value', 'field'],
            fieldSettings: {
              maxLength: 50
            }
          },
          str3: {
            label: 'String',
            type: 'text',
            valueSources: ['value', 'field'],
            fieldSettings: {
              maxLength: 50
            }
          }
        }
      },
      SUBSTRING: {
        label: functionNames?.SUBSTRING || 'SubString',
        returnType: 'text',
        jsonLogic: 'subString',
        operators: textOperators,
        renderBrackets: ['\u00A0(', '\u00A0)'],
        renderSeps: ['\u00A0Start', '\u00A0End'],
        fieldSettings: {
          maxLength: 50
        },
        args: {
          field: {
            label: 'String',
            type: 'text',
            valueSources: ['field', 'value'],
            fieldSettings: {
              maxLength: 50
            }
          },
          start: {
            label: 'Start',
            type: 'number',
            valueSources: ['value'],
            fieldSettings: {
              min: 0
            }
          },
          end: {
            label: 'End',
            type: 'number',
            valueSources: ['value'],
            fieldSettings: {
              min: 0
            }
          }
        }
      },
      // FIN-12395 — LEFT(field, n): first n characters. Unambiguous prefix
      // extraction; BE clamps n to [0, length] with no exceptions.
      LEFT: {
        label: functionNames?.LEFT || 'Left',
        returnType: 'text',
        jsonLogic: 'left',
        operators: textOperators,
        renderBrackets: [' (', ' )'],
        fieldSettings: {
          maxLength: 50
        },
        args: {
          field: {
            label: 'String',
            type: 'text',
            valueSources: ['field', 'value'],
            fieldSettings: {
              maxLength: 50
            }
          },
          n: {
            label: 'Number of characters',
            type: 'number',
            valueSources: ['value'],
            fieldSettings: {
              min: 0
            }
          }
        }
      },
      // FIN-12395 — RIGHT(field, n): last n characters. Intuitive suffix
      // extraction (unlike Substring); BE clamps n to [0, length], no exceptions.
      RIGHT: {
        label: functionNames?.RIGHT || 'Right',
        returnType: 'text',
        jsonLogic: 'right',
        operators: textOperators,
        renderBrackets: [' (', ' )'],
        fieldSettings: {
          maxLength: 50
        },
        args: {
          field: {
            label: 'String',
            type: 'text',
            valueSources: ['field', 'value'],
            fieldSettings: {
              maxLength: 50
            }
          },
          n: {
            label: 'Number of characters',
            type: 'number',
            valueSources: ['value'],
            fieldSettings: {
              min: 0
            }
          }
        }
      }
    }
  },
  date: {
    type: '!struct',
    label: 'Date',
    subfields: {
      RELATIVE_DATETIME: {
        ...BasicFuncs.RELATIVE_DATETIME,
        label: functionNames?.RELATIVE_DATETIME || 'Relative',
        operators: datetimeOperators,
        args: {
          ...BasicFuncs.RELATIVE_DATETIME.args,
          date: {
            ...BasicFuncs.RELATIVE_DATETIME.args.date
          }
        }
      },
      PART_EXTRACTION: {
        label: functionNames?.PART_EXTRACTION || 'Extract from timestamp',
        returnType: 'number',
        jsonLogic: 'partExtraction',
        operators: numberOperators,
        renderBrackets: ['', ''],
        renderSeps: ['\u00A0 '],
        args: {
          field: {
            label: 'Date',
            type: 'datetime',
            // BE TimestampPartExtractionQueryFunction only accepts a field reference,
            // not a literal value (validate() rejects ValueType args). FIN-7670.
            valueSources: ['field'],
            spelEscapeForFormat: true
          },
          timestampPart: {
            label: 'Timestamp part',
            type: 'select',
            defaultValue: 'date',
            valueSources: ['value'],
            fieldSettings: {
              listValues: {
                date: 'Date',
                day_of_week: 'Day of Week',
                hour: 'Hour',
                min: 'Minute',
                mins_of_day: 'Minutes of Day',
                month: 'Month',
                sec: 'Second'
              }
            },
            spelEscapeForFormat: false
          }
        }
      },
      // FIN-13190 — `date.AGE` is the second function that exists in the engine and
      // in metadata_dropdown but was missing from this catalogue. Returns whole
      // years between the chosen date field and the event timestamp.
      AGE: {
        label: functionNames?.AGE || 'Age',
        returnType: 'number',
        jsonLogic: 'age',
        operators: numberOperators,
        renderBrackets: [' (', ' )'],
        formatFunc: (args) => `Age(${Object.values(args).join('')})`,
        args: {
          // BE: AgeQueryFunctionArgs(dateField: StringArgElement); validated
          // against Date/Datetime field types only.
          dateField: {
            label: 'Date field',
            type: 'datetime',
            valueSources: ['field'],
            preferWidgets: ['field']
          }
        }
      }
    }
  },
  number: {
    type: '!struct',
    label: 'Number',
    subfields: {
      LINEAR_REGRESSION: {
        ...BasicFuncs.LINEAR_REGRESSION,
        label: functionNames?.LINEAR_REGRESSION || 'Linear Regression',
        operators: numberOperators,
        args: {
          ...BasicFuncs.LINEAR_REGRESSION.args,
          val: {
            label: 'Value',
            type: 'number',
            valueSources: ['field'],
            // Need to set this "preferWidgets" to show field widget instead of list_range.
            // list_range included in number widget and it's act default for formula args
            preferWidgets: ['field']
          }
        }
      },
      MULTIPLE_OF_10: {
        label: functionNames?.MULTIPLE_OF_10 || 'Multiple of 10^x',
        returnType: 'boolean',
        jsonLogic: 'multipleOf10x',
        operators: booleanOperators,
        renderBrackets: ['\u00A0\u00A0', ''],
        renderSeps: ['\u00A0on\u00A0'],
        args: {
          multiplyOfLog: {
            label: '10^x',
            type: 'multiselect',
            valueSources: ['value'],
            fieldSettings: {
              listValues: [
                { label: '0', value: '0' },
                { label: '1', value: '1' },
                { label: '2', value: '2' },
                { label: '3', value: '3' },
                { label: '4', value: '4' },
                { label: '5', value: '5' },
                { label: '6', value: '6' },
                { label: '7', value: '7' },
                { label: '8', value: '8' },
                { label: '9', value: '9' },
                { label: '10', value: '10' }
              ]
            }
          },
          field: {
            label: 'Field',
            type: 'number',
            valueSources: ['field'],
            preferWidgets: ['field']
          }
        }
      },
      // FIN-13190 — `number.SUM` has existed end-to-end in the engine (decode,
      // realtime evaluate, Spark adapter) and is already advertised to the UI via
      // metadata_dropdown, but was never added here, so it never reached the
      // function picker. That is the whole bug: a formula could not add two
      // function outputs before the comparison (`function1 + function2 > 1`).
      //
      // Arity is exactly 2. It used to be 2..5 (arg3..arg5 optional), which broke
      // every save: RAQB materialises an unset optional arg as `{valueSrc:'field'}`
      // with no `value` key rather than omitting it, and the backend ArgElement
      // decoder needs `value`, so the whole rule was rejected with "Missing required
      // field". N operands come from SELF-NESTING instead — `sum(sum(a, b), c)` —
      // which the engine has always evaluated recursively and which has no arity
      // ceiling. Keeping the arg map free of optional entries makes that failure
      // unrepresentable rather than merely filtered out.
      SUM: {
        label: functionNames?.SUM || 'Sum',
        returnType: 'number',
        jsonLogic: '+',
        operators: numberOperators,
        // Nesting gives full expression power with no grammar work — the BE
        // already evaluates a QueryFunctionSrc nested in a SUM arg recursively.
        allowSelfNesting: true,
        renderBrackets: [' (', ' )'],
        renderSeps: [' + ', ' + ', ' + ', ' + '],
        // renderBrackets/renderSeps style the in-builder rendering only; the Logic
        // Summary comes from Utils.queryString, which honours formatFunc and would
        // otherwise print "Sum(Value 1: function1, Value 2: function2)".
        formatFunc: (args) => formatInfix(args, ' + '),
        args: {
          arg1: sumArg('Value 1'),
          arg2: sumArg('Value 2')
        }
      },
      // FIN-13190 — subtraction is not commutative, so its operands are named rather
      // than numbered: the names carry the order through to the backend.
      SUBTRACT: {
        label: functionNames?.SUBTRACT || 'Subtract',
        returnType: 'number',
        jsonLogic: '-',
        operators: numberOperators,
        allowSelfNesting: true,
        renderBrackets: [' (', ' )'],
        renderSeps: [' − '],
        formatFunc: (args) => `${args.minuend} − ${args.subtrahend}`,
        args: {
          minuend: sumArg('Value'),
          subtrahend: sumArg('Minus')
        }
      },
      // FIN-13190 — multiplication is associative, so it takes exactly 2 operands
      // and composes by nesting, same as SUM above and for the same save-breaking
      // reason. Also the way `f2 > 0.99 * f1` (the concentration rule shape) becomes
      // expressible.
      MULTIPLY: {
        label: functionNames?.MULTIPLY || 'Multiply',
        returnType: 'number',
        jsonLogic: '*',
        operators: numberOperators,
        allowSelfNesting: true,
        renderBrackets: [' (', ' )'],
        renderSeps: [' × '],
        formatFunc: (args) => formatInfix(args, ' × '),
        args: {
          arg1: sumArg('Value 1'),
          arg2: sumArg('Value 2')
        }
      }
    }
  },
  geolocation: {
    type: '!struct',
    label: 'Geo Location',
    subfields: {
      DISTANCE: {
        label: functionNames?.DISTANCE || 'Geo distance',
        returnType: 'number',
        jsonLogic: 'geoDistance',
        operators: numberOperators,
        renderBrackets: ['\u00A0( Start Latitude', '\u00A0)'],
        renderSeps: [
          '\u00A0Start Longitude',
          '\u00A0End Latitude',
          '\u00A0End Longitude'
        ],
        fieldSettings: {
          min: -999999.9999,
          max: 999999.9999
        },
        args: {
          startLatitude: {
            label: 'Start Latitude',
            type: 'number',
            valueSources: ['field', 'value'],
            preferWidgets: ['field'],
            fieldSettings: {
              min: -999.999999,
              max: 999.999999
            }
          },
          startLongitude: {
            label: 'Start Longitude',
            type: 'number',
            valueSources: ['field', 'value'],
            preferWidgets: ['field'],
            fieldSettings: {
              min: -999.999999,
              max: 999.999999
            }
          },
          endLatitude: {
            label: 'End Latitude',
            type: 'number',
            valueSources: ['field', 'value'],
            preferWidgets: ['field'],
            fieldSettings: {
              min: -999.999999,
              max: 999.999999
            }
          },
          endLongitude: {
            label: 'End Longitude',
            type: 'number',
            valueSources: ['field', 'value'],
            preferWidgets: ['field'],
            fieldSettings: {
              min: -999.999999,
              max: 999.999999
            }
          }
        }
      }
    }
  }
});

const ctx = InitialConfig.ctx;

export const config = (
  matchList,
  allFunc = [],
  functionNames = {},
  isFieldWithFunction
) => ({
  ctx,
  conjunctions,
  operators: operators(isFieldWithFunction),
  widgets: widgetsData(matchList, allFunc),
  types,
  funcs: funcs(functionNames)
});

export const triggeredOnOptions = [
  {
    label: 'Transaction',
    value: 'transaction'
  },
  {
    label: 'Activity',
    value: 'activity'
  }
];
